import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { MaintenanceWindow } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { formatShortDateTime } from '../utils/dateFormat';

interface AppErrorPageProps {
  forceMaintenance?: MaintenanceWindow;
}

export function AppErrorPage({ forceMaintenance }: AppErrorPageProps = {}) {
  const navigate = useNavigate();
  const params = useParams();
  const { t, language } = useLanguage();
  const kind = forceMaintenance ? 'server' : params.kind;
  const [maintenance, setMaintenance] = useState<MaintenanceWindow | null>(forceMaintenance ?? null);

  useEffect(() => {
    if (forceMaintenance) {
      setMaintenance(forceMaintenance);
      return;
    }
    if (kind !== 'server') return;

    const check = () => {
      api.getMaintenanceStatus()
        .then((data) => {
          if (data.status === 'active' || data.status === 'upcoming') {
            setMaintenance(data.window);
          }
          // Stay on this page while maintenance is actively blocking access.
          if (data.status === 'active') return;
          // Backend is reachable and not in active maintenance — recover.
          const returnTo = (() => {
            try { return sessionStorage.getItem('forxa_return_from_error') || '/dashboard'; } catch { return '/dashboard'; }
          })();
          try { sessionStorage.removeItem('forxa_return_from_error'); } catch { /* ignore */ }
          navigate(returnTo, { replace: true });
        })
        .catch(() => {/* backend still unreachable — keep polling */});
    };

    check(); // immediate check handles the "reload while healthy" case
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  }, [forceMaintenance, kind, navigate]);

  const cfg = useMemo(() => {
    const map: Record<string, { code: string; label: string; headline: string; message: string }> = {
      forbidden: {
        code: '403',
        label: t('error.forbidden.label'),
        headline: t('error.forbidden.headline'),
        message: t('error.forbidden.message'),
      },
      server: {
        code: '503',
        label: t('error.server.label'),
        headline: t('error.server.headline'),
        message: t('error.server.message'),
      },
      data: {
        code: '500',
        label: t('error.data.label'),
        headline: t('error.data.headline'),
        message: t('error.data.message'),
      },
      default: {
        code: '404',
        label: t('error.default.label'),
        headline: t('error.default.headline'),
        message: t('error.default.message'),
      },
    };
    return map[kind ?? ''] ?? map.default;
  }, [kind, t]);

  const d = cfg.code.split('');
  const maintenanceReturn = maintenance?.ends_at
    ? formatShortDateTime(maintenance.ends_at, language)
    : null;

  return (
    <div
      className="min-h-screen flex flex-col overflow-hidden relative"
      style={{ background: '#0D1F2D', fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg,transparent 0 39px,rgba(255,255,255,0.03) 39px 40px),' +
            'repeating-linear-gradient(90deg,transparent 0 39px,rgba(255,255,255,0.03) 39px 40px)',
        }}
      />

      <div className="relative z-10 flex-shrink-0 overflow-hidden" style={{ height: 28 }}>
        <HazardStripe />
      </div>

      <div className="relative z-10 px-10 pt-8 flex items-center gap-3">
        <div className="w-8 h-8 flex-shrink-0">
          <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
            <g transform="translate(100 100) rotate(45)">
              <rect x="-86" y="-22" width="172" height="44" rx="4" fill="#F97316" />
              <circle cx="-66" cy="-10" r="3" fill="#0D1F2D" opacity="0.6" />
              <circle cx="-66" cy="10" r="3" fill="#0D1F2D" opacity="0.6" />
              <circle cx="66" cy="-10" r="3" fill="#0D1F2D" opacity="0.6" />
              <circle cx="66" cy="10" r="3" fill="#0D1F2D" opacity="0.6" />
            </g>
            <g transform="translate(100 100) rotate(-45)">
              <rect x="-86" y="-22" width="172" height="44" rx="4" fill="#F97316" />
              <circle cx="-66" cy="-10" r="3" fill="#0D1F2D" opacity="0.6" />
              <circle cx="-66" cy="10" r="3" fill="#0D1F2D" opacity="0.6" />
              <circle cx="66" cy="-10" r="3" fill="#0D1F2D" opacity="0.6" />
              <circle cx="66" cy="10" r="3" fill="#0D1F2D" opacity="0.6" />
            </g>
            <circle cx="100" cy="100" r="6" fill="#0D1F2D" />
            <circle cx="100" cy="100" r="2.5" fill="#F97316" />
          </svg>
        </div>
        <span className="font-bold tracking-[0.14em] text-white text-lg">
          FOR<span style={{ color: '#F97316' }}>X</span>A
        </span>
      </div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center py-12">
        <div
          className="flex items-center justify-center font-black leading-none mb-6 select-none"
          style={{ fontSize: 'clamp(7rem, 18vw, 14rem)', color: '#F1F5F9', letterSpacing: '-0.04em' }}
        >
          <span>{d[0]}</span>
          <span className="mx-2 inline-flex items-center justify-center" style={{ fontSize: '0.7em', color: '#F97316', position: 'relative', top: '0.02em' }}>
            <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" style={{ width: '0.75em', height: '0.75em' }}>
              <g transform="translate(100 100) rotate(45)">
                <rect x="-86" y="-22" width="172" height="44" rx="6" fill="#F97316" />
                <circle cx="-66" cy="-10" r="4" fill="#0D1F2D" opacity="0.6" />
                <circle cx="-66" cy="10" r="4" fill="#0D1F2D" opacity="0.6" />
                <circle cx="66" cy="-10" r="4" fill="#0D1F2D" opacity="0.6" />
                <circle cx="66" cy="10" r="4" fill="#0D1F2D" opacity="0.6" />
              </g>
              <g transform="translate(100 100) rotate(-45)">
                <rect x="-86" y="-22" width="172" height="44" rx="6" fill="#F97316" />
                <circle cx="-66" cy="-10" r="4" fill="#0D1F2D" opacity="0.6" />
                <circle cx="-66" cy="10" r="4" fill="#0D1F2D" opacity="0.6" />
                <circle cx="66" cy="-10" r="4" fill="#0D1F2D" opacity="0.6" />
                <circle cx="66" cy="10" r="4" fill="#0D1F2D" opacity="0.6" />
              </g>
              <circle cx="100" cy="100" r="8" fill="#0D1F2D" />
              <circle cx="100" cy="100" r="3.5" fill="#F97316" />
            </svg>
          </span>
          <span>{d[2]}</span>
        </div>

        <p className="text-xs font-bold tracking-[0.28em] uppercase mb-4" style={{ color: '#F97316' }}>
          {'// '}{cfg.label}
        </p>

        <h1
          className="font-bold mb-4 leading-tight"
          style={{ fontSize: 'clamp(1.4rem, 3vw, 2.2rem)', color: '#F1F5F9', maxWidth: 600 }}
        >
          {cfg.headline}
        </h1>

        <p className="text-base leading-relaxed mb-4 max-w-md" style={{ color: 'rgba(255,255,255,0.45)' }}>
          {maintenance?.message || cfg.message}
        </p>

        {maintenanceReturn ? (
          <p className="text-sm font-semibold mb-10" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {t('error.weWillBeBack', { value: maintenanceReturn })}
          </p>
        ) : (
          <div className="mb-10" />
        )}

        <div className="flex items-center gap-3 flex-wrap justify-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 rounded-xl font-bold text-sm transition-colors"
            style={{ background: '#F97316', color: '#0D1F2D' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#ea6c10'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#F97316'; }}
          >
            {t('error.dashboard')} {'->'}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 rounded-xl font-bold text-sm transition-colors"
            style={{ background: 'transparent', border: '1.5px solid #294358', color: '#94A3B8' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#3E5A70';
              e.currentTarget.style.color = '#F1F5F9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#294358';
              e.currentTarget.style.color = '#94A3B8';
            }}
          >
            {t('error.goBack')}
          </button>
        </div>
      </div>

      <div className="relative z-10 px-10 pb-4 flex items-center">
        <p className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: 'rgba(255,255,255,0.18)' }}>
          {t('error.footer', { code: cfg.code })}
        </p>
      </div>

      <div className="relative z-10 flex-shrink-0 overflow-hidden" style={{ height: 28 }}>
        <HazardStripe />
      </div>
    </div>
  );
}

function HazardStripe() {
  return (
    <div
      className="w-full h-full"
      style={{
        background: `repeating-linear-gradient(
          -45deg,
          #F97316 0px,
          #F97316 24px,
          #0D1F2D 24px,
          #0D1F2D 48px
        )`,
      }}
    />
  );
}
